const DB_NAME = 'beliruma-agent-local-data';
const DB_VERSION = 1;
const DRAFT_STORE = 'listingDrafts';
const PHOTO_STORE = 'listingDraftPhotos';
const LAST_USER_KEY = 'beliruma-agent-last-user-uuid';

let databasePromise = null;
let writeQueue = Promise.resolve();

function requireBrowserStorage() {
  if (typeof window === 'undefined' || !window.indexedDB) {
    throw new Error('IndexedDB is unavailable');
  }
}

function openDatabase() {
  requireBrowserStorage();
  if (databasePromise) return databasePromise;

  databasePromise = new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(DRAFT_STORE)) {
        database.createObjectStore(DRAFT_STORE, { keyPath: 'userUuid' });
      }
      if (!database.objectStoreNames.contains(PHOTO_STORE)) {
        const photoStore = database.createObjectStore(PHOTO_STORE, { keyPath: 'key' });
        photoStore.createIndex('draftId', 'draftId', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      databasePromise = null;
      reject(request.error || new Error('Failed to open listing draft database'));
    };
  });

  return databasePromise;
}

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
  });
}

function transactionDone(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error || new Error('IndexedDB transaction failed'));
    transaction.onabort = () =>
      reject(transaction.error || new Error('IndexedDB transaction aborted'));
  });
}

function enqueueWrite(operation) {
  const queued = writeQueue.then(operation, operation);
  writeQueue = queued.catch(() => {});
  return queued;
}

function photoKey(draftId, localId) {
  return `${draftId}:${localId}`;
}

export async function getForUser(userUuid) {
  if (!userUuid) return null;
  const database = await openDatabase();
  const transaction = database.transaction(DRAFT_STORE, 'readonly');
  return requestResult(transaction.objectStore(DRAFT_STORE).get(userUuid));
}

export function upsert(draft) {
  return enqueueWrite(async () => {
    const database = await openDatabase();
    const transaction = database.transaction(DRAFT_STORE, 'readwrite');
    const store = transaction.objectStore(DRAFT_STORE);
    const existing = await requestResult(store.get(draft.userUuid));
    if (!existing || Number(draft.revision) >= Number(existing.revision || 0)) {
      store.put(draft);
    }
    await transactionDone(transaction);
  });
}

export function putPhoto({ draftId, localId, blob, fileName, fileType }) {
  return enqueueWrite(async () => {
    const database = await openDatabase();
    const transaction = database.transaction(PHOTO_STORE, 'readwrite');
    transaction.objectStore(PHOTO_STORE).put({
      key: photoKey(draftId, localId),
      draftId,
      localId,
      blob,
      fileName,
      fileType,
    });
    await transactionDone(transaction);
  });
}

export async function getPhotos(draftId) {
  if (!draftId) return [];
  const database = await openDatabase();
  const transaction = database.transaction(PHOTO_STORE, 'readonly');
  const index = transaction.objectStore(PHOTO_STORE).index('draftId');
  return requestResult(index.getAll(draftId));
}

export function deletePhoto(draftId, localId) {
  return enqueueWrite(async () => {
    const database = await openDatabase();
    const transaction = database.transaction(PHOTO_STORE, 'readwrite');
    transaction.objectStore(PHOTO_STORE).delete(photoKey(draftId, localId));
    await transactionDone(transaction);
  });
}

async function deletePhotosInTransaction(photoStore, draftId) {
  const index = photoStore.index('draftId');
  await new Promise((resolve, reject) => {
    const cursorRequest = index.openCursor(window.IDBKeyRange.only(draftId));
    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (!cursor) {
        resolve();
        return;
      }
      cursor.delete();
      cursor.continue();
    };
    cursorRequest.onerror = () => reject(cursorRequest.error);
  });
}

export function deleteForUser(userUuid) {
  if (!userUuid || typeof window === 'undefined' || !window.indexedDB) return Promise.resolve();
  return enqueueWrite(async () => {
    const database = await openDatabase();
    const transaction = database.transaction([DRAFT_STORE, PHOTO_STORE], 'readwrite');
    const draftStore = transaction.objectStore(DRAFT_STORE);
    const draft = await requestResult(draftStore.get(userUuid));
    draftStore.delete(userUuid);
    if (draft && draft.draftId) {
      await deletePhotosInTransaction(transaction.objectStore(PHOTO_STORE), draft.draftId);
    }
    await transactionDone(transaction);
  });
}

export function rememberLastUserUuid(userUuid) {
  if (typeof window === 'undefined' || !userUuid) return;
  try {
    window.localStorage.setItem(LAST_USER_KEY, userUuid);
  } catch (_) {
    return;
  }
}

export function getLastUserUuid() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(LAST_USER_KEY);
  } catch (_) {
    return null;
  }
}

export function forgetLastUserUuid() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(LAST_USER_KEY);
  } catch (_) {
    return;
  }
}
